import {
  auth,
  db
} from "../FirebaseIndex";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithRedirect,
  OAuthProvider,
  sendPasswordResetEmail,
  signOut,
  getAdditionalUserInfo,
  signInWithCredential,
  updateProfile
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  writeBatch
} from "firebase/firestore";
import debug from '../../../utils/debug';

// Create provider instances
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

// Performance-monitoring analytics is optional. The previous implementation
// dynamically imported a module that no longer exists (Services/Analytics/AnalyticsService),
// which produced a webpack "module not found" warning on every build. It is kept as a
// no-op so existing callers continue to work without referencing the missing module.
let AnalyticsService = null;
const initAnalytics = async () => {
  return AnalyticsService;
};

// Register a new user with Firebase
// `options` carries registration-page choices:
//   - emailPreVerified: email was already verified by code before account creation
//   - legalAccepted / legalVersion: website-legals acceptance to record
//   - promotionalEmails: opt-in to updates/news/events
export const registerUser = async (email, password, displayName, userType = "Talent", options = {}) => {
  debug.firebase('AuthService', 'Starting registration', { email, userType });

  const {
    emailPreVerified = false,
    legalAccepted = false,
    legalVersion = '2026-06',
    promotionalEmails = false
  } = options;

  try {
    await initAnalytics();

    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update display name
    if (displayName) {
      await updateProfile(user, { displayName });
    }

    debug.firebase('AuthService', 'User created in Firebase Auth', { uid: user.uid });

    // Create user profile in Firestore
    const userProfileData = {
      userID: user.uid,
      email: user.email,
      name: displayName || user.displayName || 'New User',
      profile: {
        name: displayName || user.displayName || 'New User',
        email: user.email,
        userType: userType,
        registrationDate: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        isActive: true,
        // When the email was pre-verified by code, the auth flag is flipped by the
        // finalizeVerifiedSignup callable; mirror that verified state here too.
        isVerified: emailPreVerified,
        emailVerified: emailPreVerified,
        hasCompletedOnboarding: false,
        registrationMethod: 'email',
        registrationStatus: emailPreVerified ? 'active' : 'email_verification_pending',
        legalAcceptance: {
          accepted: !!legalAccepted,
          acceptedAt: serverTimestamp(),
          version: legalVersion
        }
      },
      settings: {
        notifications: {
          email: true,
          sms: false,
          push: true,
          promotionalEmails: !!promotionalEmails
        },
        privacy: {
          profileVisibility: 'public',
          showEmail: false,
          showPhone: false
        }
      },
      metadata: {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp()
      }
    };

    await setDoc(doc(db, "Users", user.uid), userProfileData);
    debug.firebase('AuthService', 'User profile created in Firestore');

    // Only send the native link verification email when the email was NOT already
    // verified by code during signup (legacy/fallback path).
    if (!emailPreVerified) {
      await sendVerificationEmail();
    }

    // Track registration
    if (AnalyticsService) {
      await AnalyticsService.logCustomEvent('user_registration', {
        method: 'email',
        user_type: userType
      });
    }

    return {
      success: true,
      user: user,
      userType: userType,
      isVerified: false,
      message: "Registration successful! Please check your email to verify your account."
    };

  } catch (error) {
    debug.error('AuthService', 'Registration error', error);

    if (AnalyticsService) {
      await AnalyticsService.logCustomEvent('registration_error', {
        error_code: error.code,
        method: 'email'
      });
    }

    throw error;
  }
};

// Login function
export const loginUser = async (email, password) => {
  debug.firebase('AuthService', 'Starting login', { email });

  try {
    await initAnalytics();

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    debug.firebase('AuthService', 'Firebase Auth login successful', { uid: user.uid });

    // Get user profile from Firestore
    const userDoc = await getDoc(doc(db, "Users", user.uid));

    if (!userDoc.exists()) {
      debug.error('AuthService', 'User profile not found in Firestore', { uid: user.uid });
      throw new Error("User profile not found");
    }

    const userData = userDoc.data();
    const userType = userData?.profile?.userType || "Talent";

    // Update last login
    await updateDoc(doc(db, "Users", user.uid), {
      "metadata.lastLoginAt": serverTimestamp()
    });

    // Track login
    if (AnalyticsService) {
      await AnalyticsService.logCustomEvent('user_login', {
        method: 'email',
        user_type: userType,
        is_verified: user.emailVerified
      });
    }

    return {
      success: true,
      user: user,
      userType: userType,
      isVerified: user.emailVerified,
      hasCompletedOnboarding: userData?.profile?.hasCompletedOnboarding || false
    };

  } catch (error) {
    debug.error('AuthService', 'Login error', error);

    if (AnalyticsService) {
      await AnalyticsService.logCustomEvent('login_error', {
        error_code: error.code,
        method: 'email'
      });
    }

    throw error;
  }
};

// Helper function for legacy Google Sign-In
const tryLegacyGoogleSignIn = async (preferredUserType, authWindowOptions) => {
  debug.firebase('AuthService', 'Attempting legacy Google sign-in');

  try {
    const userCredential = await signInWithPopup(auth, googleProvider);
    const user = userCredential.user;
    const additionalUserInfo = getAdditionalUserInfo(userCredential);
    const isNewUser = additionalUserInfo?.isNewUser || false;

    debug.firebase('AuthService', 'Legacy Google sign-in successful', {
      uid: user.uid,
      isNewUser,
      email: user.email
    });

    // Handle new vs existing user
    return await handleSocialAuthUser(user, isNewUser, preferredUserType || 'Talent', 'google');

  } catch (popupError) {
    debug.error('AuthService', 'Legacy popup error', popupError);

    // Try redirect as fallback
    if (popupError.code === 'auth/popup-blocked' ||
        popupError.code === 'auth/popup-closed-by-user' ||
        popupError.code === 'auth/cancelled-popup-request') {

      debug.log('AuthService', 'Attempting redirect flow instead');
      await signInWithRedirect(auth, googleProvider);
      return {
        success: true,
        redirect: true,
        message: 'Redirecting to Google sign-in...'
      };
    }

    throw popupError;
  }
};

// Google Sign-In
export const signInWithGoogle = async (preferredUserType = 'Talent') => {
  debug.firebase('AuthService', 'Starting Google sign-in', { preferredUserType });

  try {
    await initAnalytics();

    // Try to sign in with popup (legacy method) which is more reliable
    try {
      // Add popup options to mitigate COOP policy issues
      const authWindowOptions = {
        popup: {
          location: 'yes',
          height: 600,
          width: 450,
          top: window.innerHeight / 2 - 300,
          left: window.innerWidth / 2 - 225
        }
      };

      debug.firebase('AuthService', 'Using legacy Google sign-in popup with options', authWindowOptions);
      return await tryLegacyGoogleSignIn(preferredUserType, authWindowOptions);
    } catch (popupError) {
      // If popup is blocked or fails, log the error
      debug.error('AuthService', 'Popup error, attempting to use GIS flow as fallback', popupError);

      // Fall back to GIS flow
      return new Promise((resolve, reject) => {
        try {
          // Check if google is available
          if (!window.google || !window.google.accounts || !window.google.accounts.id) {
            debug.error('AuthService', 'Google Identity Services not available');
            throw new Error("Google Identity Services not available. Please try again later.");
          }

          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: async (response) => {
              try {
                if (response.credential) {
                  debug.log('AuthService', 'Received Google credential');

                  // Create GoogleAuthProvider credential with the ID token
                  const credential = GoogleAuthProvider.credential(response.credential);

                  // Sign in to Firebase with the credential
                  const userCredential = await signInWithCredential(auth, credential);
                  const user = userCredential.user;
                  const additionalUserInfo = getAdditionalUserInfo(userCredential);
                  const isNewUser = additionalUserInfo?.isNewUser || false;

                  debug.firebase('AuthService', 'GIS sign-in successful', {
                    uid: user.uid,
                    isNewUser,
                    email: user.email
                  });

                  // Handle new vs existing user
                  const result = await handleSocialAuthUser(user, isNewUser, preferredUserType, 'google');
                  resolve(result);
                } else {
                  reject(new Error("No credential received from Google"));
                }
              } catch (error) {
                debug.error('AuthService', 'Error in Google callback', error);
                reject(error);
              }
            },
            auto_select: false,
            cancel_on_tap_outside: true
          });

          // Prompt the user to select an account
          debug.log('AuthService', 'Prompting Google account selection');
          window.google.accounts.id.prompt((notification) => {
            debug.log('AuthService', 'Google prompt notification', notification);

            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              debug.warn('AuthService', 'Google prompt not displayed or skipped', {
                notDisplayed: notification.isNotDisplayed(),
                skippedMoment: notification.isSkippedMoment(),
                dismissedMoment: notification.isDismissedMoment()
              });

              // Fall back to legacy popup method
              debug.log('AuthService', 'Falling back to legacy popup method');
              tryLegacyGoogleSignIn(preferredUserType)
                .then(resolve)
                .catch(reject);
            }
          });

        } catch (error) {
          debug.error('AuthService', 'Error initializing Google Identity Services', error);
          reject(error);
        }
      });
    }

  } catch (error) {
    debug.error('AuthService', 'Google sign-in error', error);

    if (AnalyticsService) {
      await AnalyticsService.logCustomEvent('social_login_error', {
        provider: 'google',
        error_code: error.code
      });
    }

    throw error;
  }
};

// Apple Sign-In
export const signInWithApple = async (preferredUserType = 'Talent') => {
  debug.firebase('AuthService', 'Starting Apple sign-in', { preferredUserType });

  try {
    await initAnalytics();

    const userCredential = await signInWithPopup(auth, appleProvider);
    const user = userCredential.user;
    const additionalUserInfo = getAdditionalUserInfo(userCredential);
    const isNewUser = additionalUserInfo?.isNewUser || false;

    debug.firebase('AuthService', 'Apple sign-in successful', {
      uid: user.uid,
      isNewUser
    });

    return await handleSocialAuthUser(user, isNewUser, preferredUserType, 'apple');

  } catch (error) {
    debug.error('AuthService', 'Apple sign-in error', error);

    if (AnalyticsService) {
      await AnalyticsService.logCustomEvent('social_login_error', {
        provider: 'apple',
        error_code: error.code
      });
    }

    // Handle specific Apple Sign-In errors
    if (error.code === 'auth/popup-blocked') {
      // Try redirect method
      await signInWithRedirect(auth, appleProvider);
      return { success: true, redirect: true };
    }

    throw error;
  }
};

// Helper function to handle social auth users
const handleSocialAuthUser = async (user, isNewUser, userType, provider) => {
  debug.firebase('AuthService', 'Handling social auth user', {
    uid: user.uid,
    isNewUser,
    userType,
    provider
  });

  try {
    if (isNewUser) {
      // Create new user profile
      const userProfileData = {
        userID: user.uid,
        email: user.email,
        name: user.displayName || 'New User',
        profile: {
          name: user.displayName || 'New User',
          email: user.email,
          userType: userType,
          registrationDate: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          isActive: true,
          isVerified: true, // Social auth users are pre-verified
          emailVerified: true,
          hasCompletedOnboarding: false,
          registrationMethod: provider,
          registrationStatus: 'profile_setup_pending'
        },
        settings: {
          notifications: {
            email: true,
            sms: false,
            push: true
          },
          privacy: {
            profileVisibility: 'public',
            showEmail: false,
            showPhone: false
          }
        },
        metadata: {
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp()
        }
      };

      await setDoc(doc(db, "Users", user.uid), userProfileData);
      debug.firebase('AuthService', 'New user profile created');

      if (AnalyticsService) {
        await AnalyticsService.logCustomEvent('user_registration', {
          method: provider,
          user_type: userType
        });
      }

      return {
        success: true,
        user: user,
        userType: userType,
        isNewUser: true,
        isVerified: true,
        hasCompletedOnboarding: false
      };

    } else {
      // Get existing user profile
      const userDoc = await getDoc(doc(db, "Users", user.uid));

      if (!userDoc.exists()) {
        // Create profile if it doesn't exist (edge case)
        debug.warn('AuthService', 'Existing user without profile, creating one');

        const userProfileData = {
          userID: user.uid,
          email: user.email,
          name: user.displayName || 'User',
          profile: {
            name: user.displayName || 'User',
            email: user.email,
            userType: userType,
            registrationDate: serverTimestamp(),
            lastUpdated: serverTimestamp(),
            isActive: true,
            isVerified: true,
            emailVerified: true,
            hasCompletedOnboarding: false,
            registrationMethod: provider,
            registrationStatus: 'profile_setup_pending'
          },
          settings: {
            notifications: {
              email: true,
              sms: false,
              push: true
            },
            privacy: {
              profileVisibility: 'public',
              showEmail: false,
              showPhone: false
            }
          },
          metadata: {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastLoginAt: serverTimestamp()
          }
        };

        await setDoc(doc(db, "Users", user.uid), userProfileData);

        return {
          success: true,
          user: user,
          userType: userType,
          isNewUser: false,
          isVerified: true,
          hasCompletedOnboarding: false
        };
      }

      const userData = userDoc.data();
      const existingUserType = userData?.profile?.userType || userType;

      // Update last login
      await updateDoc(doc(db, "Users", user.uid), {
        "metadata.lastLoginAt": serverTimestamp()
      });

      if (AnalyticsService) {
        await AnalyticsService.logCustomEvent('user_login', {
          method: provider,
          user_type: existingUserType,
          is_verified: true
        });
      }

      return {
        success: true,
        user: user,
        userType: existingUserType,
        isNewUser: false,
        isVerified: true,
        hasCompletedOnboarding: userData?.profile?.hasCompletedOnboarding || false
      };
    }
  } catch (error) {
    debug.error('AuthService', 'Error handling social auth user', error);
    throw error;
  }
};

// Send verification email using Firebase native method
export const sendVerificationEmail = async () => {
  debug.firebase('AuthService', 'Sending Firebase native verification email');

  try {
    const user = auth.currentUser;
    if (!user) {
      debug.error('AuthService', 'No user logged in');
      throw new Error("No user logged in");
    }

    // Configure action code settings for better UX
    const actionCodeSettings = {
      // URL to redirect to after verification
      url: `${window.location.origin}/email-verified?uid=${user.uid}`,
      // This must be true for email verification
      handleCodeInApp: true
    };

    // Send Firebase native verification email
    await sendEmailVerification(user, actionCodeSettings);

    debug.firebase('AuthService', 'Firebase verification email sent successfully', {
      email: user.email,
      redirectUrl: actionCodeSettings.url
    });

    return {
      success: true,
      message: "Verification email sent successfully"
    };

  } catch (error) {
    debug.error('AuthService', 'Error sending verification email', error);

    // Map Firebase errors to user-friendly messages
    let errorMessage = "Failed to send verification email";

    switch (error.code) {
      case 'auth/too-many-requests':
        errorMessage = "Too many requests. Please wait a few minutes before trying again.";
        break;
      case 'auth/user-not-found':
        errorMessage = "User account not found. Please sign up first.";
        break;
      case 'auth/invalid-email':
        errorMessage = "Invalid email address.";
        break;
      default:
        errorMessage = error.message || errorMessage;
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

// Reset password
export const resetPassword = async (email) => {
  debug.firebase('AuthService', 'Sending password reset email', { email });

  try {
    await initAnalytics();

    const actionCodeSettings = {
      url: `${window.location.origin}/login`,
      handleCodeInApp: false
    };

    await sendPasswordResetEmail(auth, email, actionCodeSettings);

    if (AnalyticsService) {
      await AnalyticsService.logCustomEvent('password_reset_requested', {
        email_domain: email.split('@')[1]
      });
    }

    return {
      success: true,
      message: "Password reset email sent successfully"
    };

  } catch (error) {
    debug.error('AuthService', 'Password reset error', error);

    if (AnalyticsService) {
      await AnalyticsService.logCustomEvent('password_reset_error', {
        error_code: error.code
      });
    }

    throw error;
  }
};

// Logout
export const logout = async () => {
  debug.firebase('AuthService', 'Logging out user');

  try {
    await initAnalytics();

    const user = auth.currentUser;
    if (user && AnalyticsService) {
      await AnalyticsService.logCustomEvent('user_logout', {
        uid: user.uid
      });
    }

    await signOut(auth);

    debug.log('AuthService', 'User logged out successfully');
    return { success: true };

  } catch (error) {
    debug.error('AuthService', 'Logout error', error);
    throw error;
  }
};

// Get current user profile from Firestore
export const getUserProfile = async (uid) => {
  debug.firebase('AuthService', 'Fetching user profile', { uid });

  try {
    const userDoc = await getDoc(doc(db, "Users", uid));

    if (userDoc.exists()) {
      return userDoc.data();
    } else {
      debug.warn('AuthService', 'User profile not found', { uid });
      return null;
    }
  } catch (error) {
    debug.error('AuthService', 'Error fetching user profile', error);
    throw error;
  }
};

// Update user profile
export const updateUserProfile = async (uid, updates) => {
  debug.firebase('AuthService', 'Updating user profile', { uid, updates });

  try {
    await updateDoc(doc(db, "Users", uid), {
      ...updates,
      "metadata.updatedAt": serverTimestamp()
    });

    debug.log('AuthService', 'User profile updated successfully');
    return { success: true };

  } catch (error) {
    debug.error('AuthService', 'Error updating user profile', error);
    throw error;
  }
};

// Initialize Google Identity Services
export const initializeGIS = async () => {
  debug.log('AuthService', 'Initializing Google Identity Services');

  return new Promise((resolve) => {
    // Check if already loaded
    if (window.google && window.google.accounts) {
      debug.log('AuthService', 'Google Identity Services already loaded');
      resolve();
      return;
    }

    // Load the GIS library
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      debug.log('AuthService', 'Google Identity Services loaded successfully');
      resolve();
    };
    script.onerror = (error) => {
      debug.error('AuthService', 'Failed to load Google Identity Services', error);
      resolve(); // Resolve anyway to not block the app
    };

    document.head.appendChild(script);
  });
};